import React, { useEffect, useRef } from "react";
import {
  Controller,
  Control,
  FieldErrors,
  FieldValues,
  useFormContext,
} from "react-hook-form";
import type { FieldDefinition } from "../../../types";
import Input from "../Input";
import RadioCardGroup from "../RadioCardGroup";
import CheckboxField from "../CheckboxField";
import CheckboxGroup from "../CheckboxGroup";
import Select from "../Select";
import CitySearchBox from "@/components/test/SearchBoxTest";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { useEvaluationFormStore } from "@/stores";

interface FieldRendererProps {
  field: FieldDefinition;
  control: Control<any>;
  errors: FieldErrors<FieldValues>;
  onAutoAdvance?: () => void; // Callback for auto-advance on selection
}

const FieldRenderer: React.FC<FieldRendererProps> = ({
  field,
  control,
  errors,
  onAutoAdvance,
}) => {
  const error = errors[field.name]?.message as string | undefined;

  // Render based on field type
  switch (field.type) {
    case "text":
      return (
        <Controller
          name={field.name}
          control={control}
          defaultValue={field.defaultValue || ""}
          render={({ field: fieldProps }) => (
            <Input
              {...fieldProps}
              variant="normal"
              type="text"
              label={field.label}
              placeholder={field.placeholder}
              error={error}
            />
          )}
        />
      );

    case "number":
      return (
        <Controller
          name={field.name}
          control={control}
          defaultValue={field.defaultValue || ""}
          render={({ field: fieldProps }) => (
            <Input
              {...fieldProps}
              variant="normal"
              type="number"
              label={field.label}
              placeholder={field.placeholder}
              error={error}
            />
          )}
        />
      );

    case "increment": {
      const incrementField = field as Extract<
        FieldDefinition,
        { type: "increment" }
      >;
      return (
        <Controller
          name={field.name}
          control={control}
          defaultValue={field.defaultValue || "0"}
          render={({ field: fieldProps }) => (
            <Input
              {...fieldProps}
              variant="increment"
              label={field.label}
              placeholder={field.placeholder}
              error={error}
              min={incrementField.min}
              max={incrementField.max}
              onIncrement={() => {
                const currentValue = parseInt(fieldProps.value || "0", 10);
                fieldProps.onChange((currentValue + 1).toString());
              }}
              onDecrement={() => {
                const currentValue = parseInt(fieldProps.value || "0", 10);
                const newValue = Math.max(
                  incrementField.min || 0,
                  currentValue - 1
                );
                fieldProps.onChange(newValue.toString());
              }}
            />
          )}
        />
      );
    }

    case "suffix": {
      const suffixField = field as Extract<FieldDefinition, { type: "suffix" }>;
      return (
        <Controller
          name={field.name}
          control={control}
          defaultValue={field.defaultValue || ""}
          render={({ field: fieldProps }) => (
            <Input
              {...fieldProps}
              variant="suffix"
              type="number"
              label={field.label}
              placeholder={field.placeholder}
              suffix={suffixField.suffix}
              error={error}
            />
          )}
        />
      );
    }

    case "radio-card": {
      const radioField = field as Extract<
        FieldDefinition,
        { type: "radio-card" }
      >;
      return (
        <Controller
          name={field.name}
          control={control}
          defaultValue={field.defaultValue || ""}
          render={({ field: fieldProps }) => {
            const handleChange = (value: string) => {
              fieldProps.onChange(value);
              
              // Navigate if onSelectNavigate is provided
              if (radioField.onSelectNavigate) {
                window.location.href = radioField.onSelectNavigate;
                return;
              }
              
              // Auto-advance if enabled and callback provided
              if (radioField.autoAdvance && onAutoAdvance) {
                // Small delay to ensure form state is updated
                setTimeout(() => {
                  onAutoAdvance();
                }, 100);
              }
            };
            return (
              <RadioCardGroup
                {...fieldProps}
                value={fieldProps.value}
                onChange={handleChange}
                options={radioField.options}
                error={error}
                columns={radioField.columns}
              />
            );
          }}
        />
      );
    }

    case "checkbox": {
      const checkboxField = field as Extract<
        FieldDefinition,
        { type: "checkbox" }
      >;
      return (
        <Controller
          name={field.name}
          control={control}
          defaultValue={field.defaultValue || false}
          render={({ field: fieldProps }) => (
            <CheckboxField
              {...fieldProps}
              checked={fieldProps.value}
              onChange={fieldProps.onChange}
              label={checkboxField.checkboxLabel || field.label || ""}
              error={error}
            />
          )}
        />
      );
    }

    case "checkbox-group": {
      const checkboxGroupField = field as Extract<
        FieldDefinition,
        { type: "checkbox-group" }
      >;
      return (
        <CheckboxGroup
          name={field.name}
          control={control}
          errors={errors}
          options={checkboxGroupField.options}
          label={field.label}
          error={error}
        />
      );
    }

    case "select": {
      const selectField = field as Extract<FieldDefinition, { type: "select" }>;
      return (
        <Controller
          name={field.name}
          control={control}
          defaultValue={field.defaultValue || ""}
          render={({ field: fieldProps }) => (
            <Select
              {...fieldProps}
              label={field.label}
              placeholder={field.placeholder}
              options={selectField.options}
              error={error}
            />
          )}
        />
      );
    }

    case "textarea":
      return (
        <Controller
          name={field.name}
          control={control}
          defaultValue={field.defaultValue || ""}
          render={({ field: fieldProps }) => (
            <div>
              {field.label && (
                <Label className="block text-sm font-medium mb-1">
                  {field.label}
                </Label>
              )}
              <Textarea
                {...fieldProps}
                placeholder={field.placeholder}
                className="bg-gray-100 border-gray-300"
              />
              {error && <p className="text-red-500 text-xs mt-1">{error}</p>}
            </div>
          )}
        />
      );

    case "address-search":
      return (
        <Controller
          name={field.name}
          control={control}
          render={({ field: fieldProps }) => {
            // Extract ref to avoid forwardRef warning (we don't need it)
            const { ref, ...restFieldProps } = fieldProps;
            // Get all address field errors
            const addressErrors = {
              city: errors.city?.message as string | undefined,
              street: errors.street?.message as string | undefined,
              zip_code: errors.zip_code?.message as string | undefined,
              house_number: errors.house_number?.message as string | undefined,
            };
            return (
              <AddressSearchWrapper
                {...restFieldProps}
                error={error}
                addressErrors={addressErrors}
              />
            );
          }}
        />
      );

    default:
      return null;
  }
};

// Wrapper component to sync CitySearchBox with react-hook-form
interface AddressSearchWrapperProps {
  value?: string;
  onChange: (value: string) => void;
  error?: string;
  addressErrors?: {
    city?: string;
    street?: string;
    zip_code?: string;
    house_number?: string;
  };
}

const AddressSearchWrapper: React.FC<AddressSearchWrapperProps> = ({
  onChange,
  error,
  addressErrors,
}) => {
  // Use specific selectors to avoid unnecessary re-renders
  const storeStreet = useEvaluationFormStore((state) => state.formData.street);
  const storeCity = useEvaluationFormStore((state) => state.formData.city);
  const storeZipCode = useEvaluationFormStore(
    (state) => state.formData.zip_code
  );
  const storeHouseNumber = useEvaluationFormStore(
    (state) => state.formData.house_number
  );
  const { setValue, trigger } = useFormContext();

  // Track last synced values to prevent infinite loops
  const lastSyncedRef = useRef<{
    street?: string;
    city?: string;
    zip_code?: string;
    house_number?: string;
  }>({});
  const onChangeRef = useRef(onChange);

  // Keep onChange ref up to date
  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  // Sync store changes back to react-hook-form and trigger validation
  useEffect(() => {
    // Check if address data exists in store
    if (storeStreet && storeCity && storeZipCode) {
      // Only sync if values are different from what we last synced
      const needsSync =
        lastSyncedRef.current.street !== storeStreet ||
        lastSyncedRef.current.city !== storeCity ||
        lastSyncedRef.current.zip_code !== storeZipCode ||
        lastSyncedRef.current.house_number !== storeHouseNumber;

      if (needsSync) {
        // Update last synced values
        lastSyncedRef.current = {
          street: storeStreet,
          city: storeCity,
          zip_code: storeZipCode,
          house_number: storeHouseNumber,
        };

        // Update all address-related fields in react-hook-form with validation
        setValue("city", storeCity, {
          shouldValidate: true,
          shouldDirty: true,
        });
        setValue("street", storeStreet, {
          shouldValidate: true,
          shouldDirty: true,
        });
        setValue("zip_code", storeZipCode, {
          shouldValidate: true,
          shouldDirty: true,
        });
        setValue("house_number", storeHouseNumber, {
          shouldValidate: true,
          shouldDirty: true,
        });

        // Also update the onChange for the field this is bound to
        onChangeRef.current(storeCity);

        // Trigger validation immediately to ensure it runs
        requestAnimationFrame(() => {
          trigger(["city", "street", "zip_code", "house_number"], {
            shouldFocus: false,
          });
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    storeStreet,
    storeCity,
    storeZipCode,
    storeHouseNumber,
    // setValue and trigger are stable from react-hook-form
  ]);

  // Collect all address field errors
  const allErrors = [
    error,
    addressErrors?.city,
    addressErrors?.street,
    addressErrors?.zip_code,
    addressErrors?.house_number,
  ].filter(Boolean) as string[];

  return (
    <div className="relative z-0 [&>div]:relative [&>div]:z-auto">
      <CitySearchBox className="w-full" />
      {allErrors.length > 0 && (
        <div className="mt-2 space-y-1">
          {allErrors.map((err, index) => (
            <p key={index} className="text-red-500 text-xs">
              {err}
            </p>
          ))}
        </div>
      )}
    </div>
  );
};

export default FieldRenderer;
